/**
 * /settings — LLM provider + API keys + consent + assistant mode.
 * Keys are masked in the input by default; a show-toggle flips to text.
 * Consent gate mirrors settingsStore G9 — keys only persist when
 * keysConsented is true.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { AssistantMode, LlmProvider } from '@/state/settingsStore';
import { useSettingsStore } from '@/state/settingsStore';

const PROVIDERS: ReadonlyArray<LlmProvider> = ['openai', 'anthropic', 'google'];
const MODES: ReadonlyArray<AssistantMode> = ['off', 'suggest', 'feedback', 'both'];

export function SettingsRoute() {
  const {
    llmProvider,
    apiKeys,
    assistantMode,
    keysConsented,
    setProvider,
    saveKey,
    clearKey,
    setAssistantMode,
    setKeysConsented,
  } = useSettingsStore();
  const [revealed, setRevealed] = useState<Record<LlmProvider, boolean>>({
    openai: false,
    anthropic: false,
    google: false,
  });

  return (
    <main className="min-h-dvh bg-bg text-fg flex flex-col gap-4 p-4" data-testid="settings-route">
      <header className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">설정</h1>
        <Link to="/" className="text-xs text-accent underline">
          ← 홈
        </Link>
      </header>

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold">LLM 프로바이더</h2>
        <div className="flex gap-2" data-testid="provider-row">
          {PROVIDERS.map((p) => (
            <button
              key={p}
              type="button"
              className={`flex-1 rounded-md border px-2 py-1 text-xs ${
                llmProvider === p
                  ? 'bg-accent text-bg border-accent'
                  : 'bg-bg-elev text-muted border-border'
              }`}
              onClick={() => setProvider(p)}
              data-testid={`provider-${p}`}
            >
              {p}
            </button>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold">API 키</h2>
        <p className="text-[11px] text-muted">
          키는 이 브라우저의 localStorage에만 저장됩니다. 동의 체크 해제 시 저장된 키가 삭제됩니다.
        </p>
        {PROVIDERS.map((p) => (
          <div key={p} className="flex items-center gap-2">
            <span className="w-16 text-xs font-mono text-muted">{p}</span>
            <input
              type={revealed[p] ? 'text' : 'password'}
              value={apiKeys[p]}
              onChange={(e) => saveKey(p, e.target.value)}
              className="flex-1 bg-bg-elev border border-border rounded-md px-2 py-1 text-xs font-mono"
              placeholder={`${p} api key`}
              data-testid={`apikey-${p}`}
            />
            <button
              type="button"
              onClick={() => setRevealed((r) => ({ ...r, [p]: !r[p] }))}
              className="text-[10px] text-muted hover:text-fg"
              aria-label={`${p} key 표시`}
              data-testid={`reveal-${p}`}
            >
              {revealed[p] ? '숨김' : '보기'}
            </button>
            <button
              type="button"
              onClick={() => clearKey(p)}
              className="text-[10px] text-danger hover:text-fg"
              data-testid={`clear-${p}`}
            >
              지우기
            </button>
          </div>
        ))}
        <label className="flex items-center gap-2 text-xs mt-2">
          <input
            type="checkbox"
            checked={keysConsented}
            onChange={(e) => setKeysConsented(e.target.checked)}
            data-testid="consent-checkbox"
          />
          로컬 저장 동의 (이 기기에만 저장)
        </label>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold">어시스턴트 모드</h2>
        <div className="flex gap-2" data-testid="mode-row">
          {MODES.map((m) => (
            <button
              key={m}
              type="button"
              className={`flex-1 rounded-md border px-2 py-1 text-xs ${
                assistantMode === m
                  ? 'bg-accent text-bg border-accent'
                  : 'bg-bg-elev text-muted border-border'
              }`}
              onClick={() => setAssistantMode(m)}
              data-testid={`mode-${m}`}
            >
              {m}
            </button>
          ))}
        </div>
      </section>
    </main>
  );
}
